import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import * as action from '../../../Action/BulkSeriesImport/BulkSeriesImport_Action';
import {
    BATCH_STATUS, NOT_STARTED_BATCH_STATUSES, CANCELLABLE_BATCH_STATUSES, StatusBadge,
} from './status';
import { countsByStatus, errorMessage, formatDateTime, num } from './helpers';
import SeriesTable from './SeriesTable';
import StartConfirmModal from './StartConfirmModal';
import AuditPanel from './AuditPanel';

const Tile = ({ label, value, note, tone }) => (
    <div className="bsi-card bsi-tile">
        <span className="bsi-th">{label}</span>
        <span className="bsi-tile-value" style={tone ? { color: tone } : undefined}>{value}</span>
        {note && <span style={{ fontSize: 13, color: note.tone ? '#ff7a6e' : undefined }} className={note.tone ? undefined : 'bsi-muted'}>{note.text || note}</span>}
    </div>
);

const ProgressCard = ({ counts, lastUpdated }) => {
    const complete = num(counts.COMPLETE);
    const failed = num(counts.FAILED);
    const skipped = num(counts.IGNORED_EXISTING_PATH);
    const cancelled = num(counts.CANCELLED);
    const running = num(counts.QUEUED) + num(counts.SUBMITTING) + num(counts.PROCESSING) + num(counts.DISCOVERED);
    const realTotal = complete + failed + skipped + cancelled + running;
    const pct = (v) => `${(v / (realTotal || 1)) * 100}%`;
    const legend = [
        [complete, 'complete', '#35ad65'],
        [running, 'transcoding', 'rgba(0,168,243,.55)'],
        [failed, 'failed', '#f74032'],
        [skipped, 'skipped (already imported)', 'rgba(255,255,255,.3)'],
        [cancelled, 'cancelled', 'rgba(255,255,255,.15)'],
    ];
    return (
        <div className="bsi-card" style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <span className="bsi-title" style={{ fontSize: 15 }}>{realTotal} episodes</span>
                {lastUpdated && <span className="bsi-muted" style={{ fontSize: 13 }}>Updated {lastUpdated.toLocaleTimeString()}</span>}
            </div>
            <div className="bsi-bar" style={{ height: 10 }} role="img" aria-label={legend.map(([n, l]) => `${n} ${l}`).join(', ')}>
                {legend.map(([n, , color], i) => <div key={i} style={{ width: pct(n), background: color }} />)}
            </div>
            <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', fontSize: 13 }}>
                {legend.filter(([n], i) => n > 0 || i < 3).map(([n, l, color]) => (
                    <span key={l} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ width: 10, height: 10, borderRadius: 3, background: color }} />{n} {l}
                    </span>
                ))}
            </div>
        </div>
    );
};

const BatchDetail = ({ batchId, canManage, onBack }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [detail, setDetail] = useState(null);
    const [items, setItems] = useState([]);
    const [summary, setSummary] = useState(null);
    const [lastUpdated, setLastUpdated] = useState(null);
    const [refreshKey, setRefreshKey] = useState(0);
    const [expanded, setExpanded] = useState({});
    const [busy, setBusy] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [showStart, setShowStart] = useState(false);
    const [showAudit, setShowAudit] = useState(false);

    const batch = detail?.batch;
    const started = batch && !NOT_STARTED_BATCH_STATUSES.includes(batch.status);

    const load = useCallback(() => Promise.all([
        dispatch(action.getBulkImportBatchDetail(batchId)),
        dispatch(action.getBulkImportSeriesItems(batchId, { page: 1, per_page: 100 })),
    ]).then(([detailRes, itemsRes]) => {
        const d = detailRes?.responseDetails || null;
        setDetail(d);
        setItems(Array.from(itemsRes?.responseDetails || []));
        setLastUpdated(new Date());
        setRefreshKey((k) => k + 1);
        if (d?.batch && NOT_STARTED_BATCH_STATUSES.includes(d.batch.status)) {
            return dispatch(action.getBulkImportStartSummary(batchId))
                .then((res) => setSummary(res?.responseDetails || null))
                .catch(() => setSummary(null));
        }
        return undefined;
    }).catch((err) => {
        setItems((prev) => prev.map((item) => ({ ...item, job_percent_complete: null, job_status: null })));
        toast.error(errorMessage(err, 'Failed to load the batch'));
    }), [dispatch, batchId]);

    useEffect(() => { load(); }, [load]);

    const refresh = async () => {
        setRefreshing(true);
        try { await load(); } finally { setRefreshing(false); }
    };

    const run = (promise, success) => {
        setBusy(true);
        return promise
            .then((res) => { if (success) success(res?.responseDetails || {}); return load(); })
            .catch((err) => toast.error(errorMessage(err, 'Something went wrong')))
            .finally(() => setBusy(false));
    };

    const recheck = () => run(dispatch(action.validateBulkImportBatch(batchId)), () => toast.success('Import checked'));

    const start = (body) => {
        setBusy(true);
        dispatch(action.startBulkImportBatch(batchId, body))
            .then(() => { toast.success('Import started'); setShowStart(false); return load(); })
            .catch((err) => {
                toast.error(errorMessage(err, 'Failed to start the import'));
                // A stale confirmation is refused — show the current numbers.
                return dispatch(action.getBulkImportStartSummary(batchId)).then((res) => setSummary(res?.responseDetails || null)).catch(() => {});
            })
            .finally(() => setBusy(false));
    };

    const retrySeries = (ids) => run(
        dispatch(action.retryBulkImportSeriesItems(batchId, { series_item_ids: ids })),
        ({ retried = [], skipped = [] }) => {
            if (retried.length) toast.success(`${retried.length} series re-queued — only what failed is resubmitted`);
            skipped.forEach((s) => toast.warn(`Series item ${s.seriesItemId}: ${s.reason}`));
        },
    );

    const cancel = () => Swal.fire({
        title: 'Cancel this import?',
        text: 'Everything not finished stops, including transcodes already running in MediaConvert. Finished series and episodes stay as they are.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, cancel it',
        cancelButtonText: 'Keep it',
    }).then((r) => {
        if (!r.isConfirmed) return;
        run(dispatch(action.cancelBulkImportBatch(batchId)), ({ jobsCancelled = 0 }) => {
            toast.success(`Import cancelled${jobsCancelled ? ` · ${jobsCancelled} running transcode(s) stopped` : ''}`);
        });
    });

    const downloadReport = () => action.downloadBulkImportReport(batchId).then((res) => {
        const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
        const link = document.createElement('a');
        link.href = url;
        link.download = `bulk-import-batch-${batchId}.csv`;
        link.click();
        URL.revokeObjectURL(url);
    }).catch(() => toast.error('Failed to download the report'));

    const closeAudit = useCallback(() => setShowAudit(false), []);
    const closeStart = useCallback(() => setShowStart(false), []);
    const toggleExpand = (id) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

    if (!batch) {
        return (
            <>
                <div className="bsi-crumb"><button type="button" className="bsi-link-btn" onClick={onBack}>Bulk Series Import</button> / Batch #{batchId}</div>
                <div className="bsi-muted">Loading…</div>
            </>
        );
    }

    const retryableSeries = items.filter((i) => i.retryable && ['FAILED', 'ROW_UPDATE_FAILED', 'COMPLETED_WITH_ERRORS'].includes(i.status));
    const readyCount = items.filter((i) => i.status === 'READY').length;
    const failedValidation = items.filter((i) => i.status === 'VALIDATION_FAILED').length;
    const warnings = items.reduce((sum, i) => sum + num(i.warning_count), 0);
    const target = batch.mode === 'ADD_EPISODES' ? items.find((i) => i.series_title)?.series_title : null;

    return (
        <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div className="bsi-crumb"><button type="button" className="bsi-link-btn" onClick={onBack}>Bulk Series Import</button> / Batch #{batch.id}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                    <h1 className="bsi-h1">{batch.label || `Batch #${batch.id}`}</h1>
                    <StatusBadge map={BATCH_STATUS} status={batch.status} />
                </div>
                <div className="bsi-muted" style={{ display: 'flex', gap: 18, flexWrap: 'wrap', fontSize: 13 }}>
                    <span>{batch.mode === 'ADD_EPISODES' ? `Add episodes${target ? ` to ${target}` : ''}` : 'New series'}</span>
                    <span style={{ textTransform: 'capitalize' }}>{batch.environment}</span>
                    <span>Source <span className="bsi-mono" style={{ color: '#b4b4c4' }}>{batch.root_uri}</span></span>
                    <span>Created {formatDateTime(batch.created_at)}</span>
                    {batch.failure_reason && <span style={{ color: '#ff7a6e' }}>{batch.failure_reason}</span>}
                </div>
            </div>

            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                {canManage && ['READY', 'READY_WITH_WARNINGS'].includes(batch.status) && (
                    <button type="button" className="bsi-btn primary" disabled={busy || !summary} onClick={() => setShowStart(true)}>
                        <i className="fas fa-play" aria-hidden="true" /> Start import
                    </button>
                )}
                {canManage && ['DRAFT', 'READY', 'READY_WITH_WARNINGS', 'INVALID'].includes(batch.status) && (
                    <button type="button" className="bsi-btn ghost" disabled={busy} onClick={recheck}>Check again</button>
                )}
                {canManage && started && retryableSeries.length > 0 && batch.status !== 'CANCELLED' && (
                    <button type="button" className="bsi-btn outline" disabled={busy} onClick={() => retrySeries(retryableSeries.map((i) => i.id))}>
                        Retry failed series ({retryableSeries.length})
                    </button>
                )}
                {/* No auto-refresh — the admin refreshes to see the latest transcoding status. */}
                <button type="button" className={`bsi-btn ${started ? 'success' : 'ghost'}`} disabled={busy || refreshing} aria-busy={refreshing} onClick={refresh}>
                    <i className={`fas fa-sync-alt${refreshing ? ' fa-spin' : ''}`} aria-hidden="true" /> {refreshing ? 'Refreshing…' : 'Refresh'}
                </button>
                <button type="button" className="bsi-btn ghost" onClick={() => setShowAudit(true)}>Audit log</button>
                <button type="button" className="bsi-btn ghost" onClick={downloadReport}><i className="fas fa-download" aria-hidden="true" /> Report</button>
                <span style={{ flexGrow: 1 }} />
                {canManage && CANCELLABLE_BATCH_STATUSES.includes(batch.status) && (
                    <button type="button" className="bsi-btn danger" disabled={busy} onClick={cancel}>Cancel import</button>
                )}
            </div>

            {!started ? (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 14 }}>
                    <Tile label="Series" value={<>{readyCount} <span className="bsi-muted" style={{ fontSize: 14, fontWeight: 500 }}>ready</span></>}
                        note={failedValidation ? { text: `${failedValidation} can't be imported`, tone: true } : 'all importable'} />
                    <Tile label="Episodes to import" value={summary?.episodeCount ?? '—'} note={`across ${readyCount} series`} />
                    <Tile label="Already imported" value={summary?.alreadyImportedCount ?? '—'} note="will be skipped" />
                    <Tile label="Warnings" value={warnings} tone={warnings ? '#facc15' : undefined} note="don't block the import" />
                </div>
            ) : (
                <ProgressCard counts={countsByStatus(detail.episodeItemCounts)} lastUpdated={lastUpdated} />
            )}

            {batch.status === 'VALIDATING' && <div className="bsi-callout blue">Checking the folders in S3… press Refresh to see the result.</div>}

            <SeriesTable
                batch={batch} items={items} started={started} canManage={canManage}
                expanded={expanded} onToggle={toggleExpand} onGoToSeries={(id) => navigate(`/series/${id}`)}
                onRetrySeries={retrySeries} refreshKey={refreshKey} refreshing={refreshing} onChanged={() => load()}
            />

            {started && items.some((item) => item.series_id) && (
                <div className="bsi-callout blue" role="note">
                    <i className="fas fa-info-circle" aria-hidden="true" />
                    <span>Click “Go to Series” to review the imported Series details and resolve any Episode upload errors.</span>
                </div>
            )}

            {showStart && <StartConfirmModal summary={summary} busy={busy} onCancel={closeStart} onConfirm={start} />}
            {showAudit && <AuditPanel batchId={batch.id} onClose={closeAudit} />}
        </>
    );
};

export default BatchDetail;
